import { useState, useEffect } from "react";
import { Landmark, Loader2, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const fmtDE = (iso) => {
    if (!iso) return "";
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split("-");
    return (y && m && d) ? `${d}.${m}.${y}` : s;
};

const STATUS_COLORS = {
    docs_open: "bg-gray-100 text-gray-600 border-gray-300",
    docs_complete: "bg-amber-100 text-amber-700 border-amber-300",
    bank_approved: "bg-sky-100 text-sky-700 border-sky-300",
    funds_received: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

// ---------- Buchhaltung: Ausstehende Bankgelder (Finanzierung/Leasing) -------
function FundRow({ row, banks, statuses, onSaved }) {
    const [f, setF] = useState({
        bank_id: row.bank_id || "",
        bank_name: row.bank_name || "",
        finance_number: row.finance_number || "",
        term_months: row.term_months ?? "",
        monthly_rate: row.monthly_rate ?? "",
        down_payment: row.down_payment ?? "",
        final_rate: row.final_rate ?? "",
        status: row.status || "docs_open",
    });
    const [saving, setSaving] = useState(false);
    const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const onBankChange = (id) => {
        const b = banks.find((x) => x.id === id);
        setF((p) => ({ ...p, bank_id: id, bank_name: b ? b.name : p.bank_name }));
    };

    const save = async (statusOverride) => {
        setSaving(true);
        try {
            await api.patch(`/listings/${row.listing_id}/dispo/financing`, statusOverride ? { ...f, status: statusOverride } : f);
            toast.success(statusOverride === "bank_approved" ? "Bank-Abnahme angestoßen" : "Finanzierungsdaten gespeichert");
            onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };

    return (
        <div className="border border-gray-200 rounded-sm p-3 bg-white space-y-2" data-testid={`bankfund-row-${row.listing_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <div className="font-bold text-gray-900 text-sm">{row.vehicle_label}</div>
                    <div className="text-xs text-gray-500">{row.customer_name}</div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 uppercase tracking-wider font-bold">Finanzierung</span>
                    <span className="font-mono-num font-black text-gray-900" data-testid={`bankfund-amount-${row.listing_id}`}>{formatPrice(row.finance_amount)}</span>
                    {row.belege_complete && <span className="text-[10px] font-bold uppercase tracking-wider rounded-sm border px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-300" data-testid={`bankfund-belege-complete-${row.listing_id}`}>Belege ✓</span>}
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-sm border px-1.5 py-0.5 ${STATUS_COLORS[f.status]}`} data-testid={`bankfund-status-badge-${row.listing_id}`}>{statuses.find((s) => s.value === f.status)?.label}</span>
                    {row.expected_date && (f.status === "bank_approved" || f.status === "funds_received") && (
                        <span className="text-[10px] font-bold uppercase tracking-wider rounded-sm border px-1.5 py-0.5 bg-sky-50 text-sky-700 border-sky-200" data-testid={`bankfund-expected-${row.listing_id}`}>Erwartet bis {fmtDE(row.expected_date)}</span>
                    )}
                    {f.status === "docs_complete" && (
                        <Button size="sm" onClick={() => save("bank_approved")} disabled={saving} className="h-7 text-[11px] bg-sky-600 hover:bg-sky-700 text-white" data-testid={`bankfund-trigger-approval-${row.listing_id}`}>
                            <Landmark className="w-3.5 h-3.5 mr-1" /> Bank-Abnahme anstoßen
                        </Button>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Bank / Leasinggeber</label>
                    {banks.length > 0 ? (
                        <select value={f.bank_id} onChange={(e) => onBankChange(e.target.value)} className="w-full h-8 text-xs rounded-sm border border-gray-300 px-1.5 bg-white" data-testid={`bankfund-bank-${row.listing_id}`}>
                            <option value="">— wählen —</option>
                            {banks.map((b) => <option key={b.id} value={b.id}>{b.name}{b.city ? ` (${b.city})` : ""}</option>)}
                        </select>
                    ) : (
                        <Input value={f.bank_name} onChange={(e) => upd("bank_name", e.target.value)} placeholder="Bankname" className="h-8 text-xs" data-testid={`bankfund-bankname-${row.listing_id}`} />
                    )}
                </div>
                <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Finanzierungsnr.</label><Input value={f.finance_number} onChange={(e) => upd("finance_number", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`bankfund-number-${row.listing_id}`} /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Laufzeit (Monate)</label><Input type="number" min="0" value={f.term_months} onChange={(e) => upd("term_months", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`bankfund-term-${row.listing_id}`} /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Monatl. Rate (€)</label><Input type="number" min="0" step="0.01" value={f.monthly_rate} onChange={(e) => upd("monthly_rate", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`bankfund-monthly-${row.listing_id}`} /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Anzahlung (€)</label><Input type="number" min="0" step="0.01" value={f.down_payment} onChange={(e) => upd("down_payment", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`bankfund-down-${row.listing_id}`} /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Schlussrate (€)</label><Input type="number" min="0" step="0.01" value={f.final_rate} onChange={(e) => upd("final_rate", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`bankfund-final-${row.listing_id}`} /></div>
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Status</label>
                    <select value={f.status} onChange={(e) => upd("status", e.target.value)} className="w-full h-8 text-xs rounded-sm border border-gray-300 px-1.5 bg-white" data-testid={`bankfund-status-${row.listing_id}`}>
                        {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
                <div className="flex items-end">
                    <Button size="sm" onClick={() => save()} disabled={saving} className="h-8 text-xs w-full" data-testid={`bankfund-save-${row.listing_id}`}>
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Speichern</>}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function OutstandingBankFundsTab() {
    const [data, setData] = useState(null);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newBank, setNewBank] = useState("");
    const [addingBank, setAddingBank] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [r, b] = await Promise.all([
                api.get("/dealer/outstanding-bank-funds"),
                api.get("/dealer/banks").catch(() => ({ data: { banks: [] } })),
            ]);
            setData(r.data);
            setBanks(b.data?.banks || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const addBank = async () => {
        if (!newBank.trim()) return;
        setAddingBank(true);
        try {
            const r = await api.post("/dealer/banks", { name: newBank.trim() });
            toast.success(`Bank „${r.data.bank.name}" hinzugefügt`);
            setNewBank("");
            const b = await api.get("/dealer/banks");
            setBanks(b.data?.banks || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Bank konnte nicht angelegt werden"); }
        finally { setAddingBank(false); }
    };

    if (loading) return <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center" data-testid="bankfunds-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>;

    const t = data?.totals || {};
    return (
        <div className="space-y-4" data-testid="bankfunds-tab">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-gray-600"><Landmark className="w-4 h-4" /> Ausstehende Bankgelder aus Finanzierung / Leasing</div>
                <div className="flex items-center gap-2">
                    <Input value={newBank} onChange={(e) => setNewBank(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addBank(); }} placeholder="+ Bank hinzufügen" className="h-8 text-xs w-44" data-testid="bankfunds-new-bank" />
                    <Button size="sm" variant="outline" onClick={addBank} disabled={addingBank} className="h-8 text-xs border-2" data-testid="bankfunds-add-bank">{addingBank ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Anlegen"}</Button>
                    <Button size="sm" variant="outline" onClick={load} className="h-8 text-xs border-2" data-testid="bankfunds-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Aktualisieren</Button>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Kpi label="Offen gesamt" value={formatPrice(data?.open_total || 0)} cls="text-[#111827]" testid="bankfunds-kpi-open" />
                <Kpi label="Unterlagen offen" value={formatPrice(t.docs_open || 0)} cls="text-gray-600" />
                <Kpi label="Vollständig" value={formatPrice(t.docs_complete || 0)} cls="text-amber-600" />
                <Kpi label="Bank-Abnahme" value={formatPrice(t.bank_approved || 0)} cls="text-sky-600" />
                <Kpi label="Eingegangen" value={formatPrice(t.funds_received || 0)} cls="text-emerald-700" />
            </div>
            {(!data?.rows || data.rows.length === 0) ? (
                <div className="text-sm text-gray-400 italic py-8 text-center" data-testid="bankfunds-empty">Keine Fahrzeuge mit Finanzierung/Leasing.</div>
            ) : (
                <div className="space-y-2">
                    {data.rows.map((row) => <FundRow key={row.listing_id} row={row} banks={banks} statuses={data.statuses} onSaved={load} />)}
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, cls = "text-[#111827]", testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm px-3 py-2" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</div>
            <div className={`text-lg font-black font-mono-num ${cls}`}>{value}</div>
        </div>
    );
}
